import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Loader2,
  Database,
  Table,
  Code,
  Search,
  ChevronLeft,
  ChevronRight,
  Activity,
  Workflow,
  Share,
  Copy,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import SortableTable from "../SortableTable";
import { useAuth } from "../../contexts/AuthContext";

const DWHSchemas = () => {
  const { fetchWithAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [schemas, setSchemas] = useState([]);
  const [filteredSchemas, setFilteredSchemas] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSchema, setSelectedSchema] = useState(null);
  const [schemaDetails, setSchemaDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false);
  const [expandedView, setExpandedView] = useState(() => {
    const saved = localStorage.getItem("dwhSchemas_expandedView");
    return saved ? JSON.parse(saved) : false;
  });

  // Fetch schemas list
  const fetchSchemas = async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth("/dwh/schemas");

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Convert schemas object to array and sort by order
      const schemasArray = Object.values(data.schemas).sort((a, b) => {
        const aName = a.name || "";
        const bName = b.name || "";

        // Check if tables are DIM_ or FACT_
        const aIsDim = aName.startsWith("DIM_");
        const bIsDim = bName.startsWith("DIM_");
        const aIsFact = aName.startsWith("FACT_");
        const bIsFact = bName.startsWith("FACT_");

        // DIM_ tables come before FACT_ tables
        if (aIsDim && bIsFact) return -1;
        if (aIsFact && bIsDim) return 1;
        // Within same category, sort by name ASC
        return aName.localeCompare(bName);
      });

      setSchemas(schemasArray);
      setFilteredSchemas(schemasArray);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Filter schemas based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredSchemas(schemas);
    } else {
      const filtered = schemas.filter((schema) => {
        const name = (schema.title || schema.name || "").toLowerCase();
        const search = searchTerm.toLowerCase();

        if (expandedView) {
          // In expanded view, search in both name and description
          const description = (schema.description || "").toLowerCase();
          return name.includes(search) || description.includes(search);
        } else {
          // In compact view, search only in name
          return name.includes(search);
        }
      });
      setFilteredSchemas(filtered);
    }
  }, [searchTerm, schemas, expandedView]);

  // Fetch schema details
  const fetchSchemaDetails = async (schemaName) => {
    try {
      setDetailsLoading(true);
      const response = await fetchWithAuth(`/dwh/schemas/${schemaName}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setSchemaDetails((prev) => ({
        ...data.schema,
        column_order: data.column_order,
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setDetailsLoading(false);
    }
  };

  useEffect(() => {
    fetchSchemas();
  }, []);

  // Load schema from URL parameter on mount and when schemas are loaded
  useEffect(() => {
    const tableParam = searchParams.get("table");
    if (tableParam && schemas.length > 0) {
      const schema = schemas.find((s) => s.name === tableParam);
      if (schema && (!selectedSchema || selectedSchema.name !== tableParam)) {
        setSelectedSchema(schema);
        fetchSchemaDetails(schema.name);
      }
    }
  }, [searchParams, schemas, selectedSchema]);

  const handleSchemaClick = (schema) => {
    setSelectedSchema(schema);
    fetchSchemaDetails(schema.name);

    // Update URL with selected table
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("table", schema.name);
    setSearchParams(newSearchParams, { replace: true });
  };

  // Copy current URL to clipboard for sharing
  const copyShareUrl = async () => {
    try {
      const currentUrl = window.location.href;
      await navigator.clipboard.writeText(currentUrl);
      // You could add a toast notification here
      console.log("URL copied to clipboard!");
    } catch (err) {
      console.error("Failed to copy URL:", err);
    }
  };

  // Toggle expanded view and save to localStorage
  const toggleExpandedView = () => {
    const newValue = !expandedView;
    setExpandedView(newValue);
    localStorage.setItem("dwhSchemas_expandedView", JSON.stringify(newValue));
  };

  // Function to highlight matching text
  const highlightText = (text, searchTerm) => {
    if (!searchTerm.trim() || !text) return text;

    const regex = new RegExp(
      `(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
      "gi"
    );
    const parts = text.split(regex);

    return parts.map((part, index) => {
      if (regex.test(part)) {
        return (
          <span
            key={index}
            className="bg-yellow-200 dark:bg-yellow-800 text-yellow-900 dark:text-yellow-100 px-1 rounded"
          >
            {part}
          </span>
        );
      }
      return part;
    });
  };

  const SchemasList = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-gray-600 dark:text-gray-400" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-red-500 dark:text-red-400 p-4">Error: {error}</div>
      );
    }

    return (
      <div className={expandedView ? "space-y-1" : "space-y-0.5"}>
        {filteredSchemas.map((schema) => (
          <div
            key={schema.name}
            className={`cursor-pointer transition-all hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg border-l-4 ${
              expandedView ? "p-3" : "px-2 py-1"
            } ${
              selectedSchema?.name === schema.name
                ? "border-l-blue-500 bg-blue-50 dark:bg-blue-900/20"
                : "border-l-transparent hover:border-l-gray-300 dark:hover:border-l-gray-600"
            }`}
            onClick={() => handleSchemaClick(schema)}
          >
            {expandedView ? (
              // Expanded view (current design)
              <div className="flex items-start space-x-3">
                <Database
                  className={`h-5 w-5 mt-0.5 flex-shrink-0 ${
                    schema.name.startsWith("DIM_")
                      ? "text-blue-600"
                      : schema.name.startsWith("FACT_")
                      ? "text-green-600"
                      : "text-gray-600"
                  }`}
                />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm dark:text-white truncate">
                    {highlightText(schema.title, searchTerm)}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                    {schema.description
                      ? highlightText(schema.description, searchTerm)
                      : "No description"}
                  </div>
                </div>
              </div>
            ) : (
              // Compact view (name only) - Ultra compact
              <div className="flex items-center space-x-1.5">
                <Database
                  className={`h-3.5 w-3.5 flex-shrink-0 ${
                    schema.name.startsWith("DIM_")
                      ? "text-blue-600"
                      : schema.name.startsWith("FACT_")
                      ? "text-green-600"
                      : "text-gray-600"
                  }`}
                />
                <div className="font-medium text-xs dark:text-white truncate leading-tight">
                  {highlightText(schema.title, searchTerm)}
                </div>
              </div>
            )}
          </div>
        ))}
        {filteredSchemas.length === 0 && !loading && (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <Database className="h-12 w-12 mx-auto mb-4 text-gray-400 dark:text-gray-500" />
            <p>No schemas found matching your search</p>
          </div>
        )}
      </div>
    );
  };

  const SchemaDetails = () => {
    if (!selectedSchema) {
      return (
        <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
          <div className="text-center">
            <Database className="h-12 w-12 mx-auto mb-4 text-gray-400 dark:text-gray-500" />
            <p>Select a schema to view details</p>
          </div>
        </div>
      );
    }

    if (detailsLoading) {
      return (
        <div className="flex items-center justify-center h-full">
          <Loader2 className="h-8 w-8 animate-spin text-gray-600 dark:text-gray-400" />
        </div>
      );
    }

    if (!schemaDetails) {
      return (
        <div className="text-red-500 dark:text-red-400 p-4">
          Failed to load schema details
        </div>
      );
    }

    // Prepare columns data for the table
    const columnsData = schemaDetails.properties
      ? (
          schemaDetails.column_order || Object.keys(schemaDetails.properties)
        ).map((name, index) => {
          const config = schemaDetails.properties[name];
          return {
            name,
            type: Array.isArray(config.type)
              ? config.type.join(" | ")
              : config.type,
            description: config.description || "",
            required: schemaDetails.required?.includes(name) || false,
            primary_key: config.primary_key || false,
            autoincrement: config.autoincrement || false,
            format: config.format || "",
            maxLength: config.maxLength || "",
            pattern: config.pattern || "",
            system: config.system || false,
            foreign_key: config.foreign_key || null,
            position: index + 1,
          };
        })
      : [];

    const tableColumns = [
      { key: "position", label: "#", sortable: true },
      { key: "name", label: "Column Name", sortable: true },
      { key: "type", label: "Type", sortable: true },
      {
        key: "system",
        label: "Type",
        sortable: true,
        render: (item) => {
          const badges = [];
          if (item.primary_key) {
            badges.push(
              <span
                key="pk"
                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 mr-1"
              >
                PK
              </span>
            );
          }
          if (item.foreign_key) {
            badges.push(
              <span
                key="fk"
                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 mr-1"
                title={`References ${item.foreign_key.table}.${item.foreign_key.column}`}
              >
                FK
              </span>
            );
          }
          if (item.system) {
            badges.push(
              <span
                key="sys"
                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200 mr-1"
              >
                SYS
              </span>
            );
          }

          // If none of the above, it's a user field
          if (!item.primary_key && !item.foreign_key && !item.system) {
            badges.push(
              <span
                key="user"
                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
              >
                USER
              </span>
            );
          }

          return badges.length > 0 ? (
            <div className="flex">{badges}</div>
          ) : null;
        },
      },
      { key: "description", label: "Description", sortable: false },
    ];

    return (
      <div className="space-y-6">
        {/* Schema Header */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Table className="h-5 w-5" />
                <span>{selectedSchema.title}</span>
              </div>
              <Button
                onClick={copyShareUrl}
                variant="ghost"
                size="sm"
                className="flex items-center space-x-1 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
                title="Copy shareable URL"
              >
                <Share className="h-4 w-4" />
                <span className="text-xs">Share</span>
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="font-medium text-sm text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {schemaDetails.description || "No description"}
                </p>
              </div>
              <div>
                <h4 className="font-medium text-sm text-gray-700 dark:text-gray-300 mb-1">
                  Statistics
                </h4>
                <div className="space-y-1">
                  <div className="text-sm">
                    <span className="text-gray-600 dark:text-gray-400">
                      Columns:{" "}
                    </span>
                    <span className="font-medium dark:text-white">
                      {columnsData.length}
                    </span>
                  </div>
                  {/* <div className="text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Required: </span>
                    <span className="font-medium dark:text-white">{schemaDetails.required?.length || 0}</span>
                  </div> */}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Columns Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Code className="h-5 w-5" />
              <span>Columns ({columnsData.length})</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <SortableTable
              data={columnsData}
              columns={tableColumns}
              defaultSort={{ key: "position", direction: "asc" }}
              exportable={true}
              exportFilename={`${selectedSchema.name}.csv`}
              globalSearch={true}
              stickyFooter={false}
              className={`flex flex-col h-full max-h-[${
                242 + columnsData.length * 42
              }px] relative`}
            />
          </CardContent>
        </Card>

        {/* Foreign Keys */}
        {(() => {
          const foreignKeys = columnsData.filter((col) => col.foreign_key);
          return (
            foreignKeys.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Foreign Keys</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {foreignKeys.map((col, index) => (
                      <div
                        key={index}
                        className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg"
                      >
                        <div className="font-medium text-sm dark:text-white">
                          {col.name}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                          References:{" "}
                          <span className="font-mono text-blue-600 dark:text-blue-400">
                            {col.foreign_key.table}.{col.foreign_key.column}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )
          );
        })()}

        {/* Unique Keys */}
        {schemaDetails.unique_keys && schemaDetails.unique_keys.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Unique Keys</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {schemaDetails.unique_keys.map((key, index) => (
                  <div
                    key={index}
                    className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                  >
                    <div className="font-medium text-sm dark:text-white">
                      {key.name}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      Columns: {key.columns.join(", ")}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  return (
    <div
      className="flex flex-col bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700"
      style={{ height: "calc(100vh - 64px)" }}
    >
      {/* Main Content - Full Screen */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Schemas List */}
        <div
          className={`${
            leftPanelCollapsed ? "w-12" : "w-1/4"
          } border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex flex-col transition-all duration-300 relative`}
        >
          {!leftPanelCollapsed && (
            <>
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 relative">
                <div className="mb-3">
                  <h2 className="text-xl font-bold dark:text-white">
                    DWH Schemas ({schemas.length})
                  </h2>
                  {selectedSchema && (
                    <div className="text-xs text-gray-600 dark:text-gray-400 mt-1 flex items-center space-x-1">
                      <span>Selected:</span>
                      <span className="font-mono bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded">
                        {selectedSchema.name}
                      </span>
                      <Copy
                        className="h-3 w-3 cursor-pointer hover:text-gray-900 dark:hover:text-gray-100"
                        onClick={copyShareUrl}
                        title="Copy URL"
                      />
                    </div>
                  )}
                </div>

                {/* View Toggle - Absolute positioned */}
                <button
                  onClick={toggleExpandedView}
                  className={`absolute top-4 right-4 px-2 py-1 text-xs rounded-md transition-colors ${
                    expandedView
                      ? "bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300"
                      : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                  } hover:bg-blue-200 dark:hover:bg-blue-800`}
                >
                  {expandedView ? "Expanded" : "Compact"}
                </button>

                {/* Search Input */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder={
                      expandedView ? "Search schemas..." : "Search by name..."
                    }
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4">
                <SchemasList />
              </div>
            </>
          )}

          {leftPanelCollapsed && (
            <div className="flex-1 flex flex-col items-center py-4 space-y-3">
              {filteredSchemas.slice(0, 10).map((schema) => (
                <button
                  key={schema.name}
                  onClick={() => handleSchemaClick(schema)}
                  className={`w-8 h-8 rounded flex items-center justify-center transition-colors ${
                    selectedSchema?.name === schema.name
                      ? "bg-blue-500 text-white"
                      : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600"
                  }`}
                  title={schema.title}
                >
                  <Database
                    className={`h-4 w-4 ${
                      schema.name.startsWith("DIM_")
                        ? "text-blue-600"
                        : schema.name.startsWith("FACT_")
                        ? "text-green-600"
                        : "text-gray-600"
                    }`}
                  />
                </button>
              ))}
            </div>
          )}

          {/* Footer Buttons */}
          <div
            className={`absolute bottom-4 ${
              leftPanelCollapsed ? "left-2 right-2" : "left-4 right-4"
            } flex ${
              leftPanelCollapsed
                ? "flex-col space-y-2"
                : "space-x-2 items-center"
            } transition-all duration-300`}
          >
            {/* DWH Visualization Button */}
            <button
              onClick={() => navigate("/dwh-visualization")}
              className={`${
                leftPanelCollapsed ? "p-2" : "px-3 py-2"
              } bg-purple-100 dark:bg-purple-900 hover:bg-purple-200 dark:hover:bg-purple-800 text-purple-700 dark:text-purple-300 rounded-md shadow-sm transition-all duration-300 z-10 flex items-center`}
              title="DWH Schema Visualization"
            >
              <Workflow
                className={leftPanelCollapsed ? "h-4 w-4" : "h-4 w-4 mr-2"}
              />
              {!leftPanelCollapsed && (
                <span className="text-sm font-medium">Visualization</span>
              )}
            </button>

            {/* DWH Validation Button */}
            <button
              onClick={() => navigate("/dwh-validation")}
              className={`${
                leftPanelCollapsed ? "p-2" : "px-3 py-2"
              } bg-blue-100 dark:bg-blue-900 hover:bg-blue-200 dark:hover:bg-blue-800 text-blue-700 dark:text-blue-300 rounded-md shadow-sm transition-all duration-300 z-10 flex items-center`}
              title="DWH Validation Report"
            >
              <Activity
                className={leftPanelCollapsed ? "h-4 w-4" : "h-4 w-4 mr-2"}
              />
              {!leftPanelCollapsed && (
                <span className="text-sm font-medium">Validation</span>
              )}
            </button>

            {/* Toggle Button */}
            <button
              onClick={() => setLeftPanelCollapsed(!leftPanelCollapsed)}
              className={`${
                leftPanelCollapsed ? "p-2" : "p-2"
              } bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full shadow-md transition-all duration-300 z-10`}
              title={leftPanelCollapsed ? "Expand panel" : "Collapse panel"}
            >
              {leftPanelCollapsed ? (
                <ChevronRight className="h-3 w-3 text-gray-600 dark:text-gray-400" />
              ) : (
                <ChevronLeft className="h-4 w-4 text-gray-600 dark:text-gray-400" />
              )}
            </button>
          </div>
        </div>

        {/* Right Panel - Schema Details */}
        <div
          className={`${
            leftPanelCollapsed ? "flex-1" : "flex-1"
          } bg-white dark:bg-gray-800 flex flex-col transition-all duration-300`}
        >
          <div className="flex-1 overflow-y-auto p-6">
            <SchemaDetails />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DWHSchemas;
